import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useSiteAdminAuth } from '@shared/providers/SiteAdminAuthContext';
import { useActiveSessionSync } from '@/features/sso/useActiveSessionSync';
import { isSsoSite } from '@/features/sso/ssoSession';

interface ProtectedSiteAdminRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedSiteAdminRoute Component
 *
 * Protects site-specific admin routes.
 * Redirects to site admin login if not authenticated.
 * Shows access denied if admin doesn't have access to the site.
 */
const ProtectedSiteAdminRoute: React.FC<ProtectedSiteAdminRouteProps> = ({ children }) => {
  const { admin, isAuthenticated, isLoading } = useSiteAdminAuth();
  const { site } = useParams<{ site: string }>();
  const location = useLocation();

  useActiveSessionSync(
    isSsoSite(site) ? { site, entryType: 'admin' } : undefined,
    isAuthenticated && !isLoading,
  );

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={`/${site}/admin/login`} state={{ from: location }} replace />;
  }

  // Check site access
  if (admin && site) {
    // Full admin can access any site
    if (admin.is_full_admin) {
      return <>{children}</>;
    }

    // Site admin can only access their own site
    if (admin.manage_site !== site) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
            <div className="text-red-500 text-5xl mb-4">
              <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-800 mb-2">アクセス権限がありません</h1>
            <p className="text-gray-600 mb-4">このサイトへのアクセス権限がありません。</p>
            {admin.manage_site && (
              <a
                href={`/${admin.manage_site}/admin`}
                className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {admin.manage_site} 管理画面へ
              </a>
            )}
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedSiteAdminRoute;
